import * as mqtt from 'mqtt'
import appConfig from '@/config'
import { eventEmitter } from '@/utils/eventEmitter'
import { handleRequestData } from './request'
import { isNil } from 'lodash'
import { ToastFail } from '@/utils/utils'
import { getLinked, getUserInfo, getToken } from '@/utils/userHelper'

export const MQTT_TOPICS = {
  frontInfo: 'frontInfo',
  hallInfo: 'hallInfo',
  ppc: 'ppc',
  playModeStatus: 'playModeStatus',
  orderList: 'orderList',
  tablePlayList: 'tablePlayList',
  tableAllList: 'tableAllList',
} as const

export const MQTT_MSG_KEYS = {
  Login: 'Login',
  pushMsg1: 'pushMsg1',
  pushMsg2: 'pushMsg2',
  pushMsg3: 'pushMsg3',
  orderList: 'orderList',
  tablePlayList: 'tablePlayList',
  tableAllList: 'tableAllList',
  playModeStatus: 'playModeStatus',
  frontInfo: 'frontInfo',
  hallInfo: 'hallInfo',
  machineInfo: 'machineInfo',
  clearRoom: 'clearRoom',
  clearService: 'clearService',
  billMsg: 'billMsg',
  roomMsg: 'roomMsg',
  roomOpenOprate: 'roomOpenOprate',
  exchangRoom: 'exchangRoom',
  noSmoking: 'noSmoking',
  setMaxVol: 'setMaxVol',
  tableOpenOprate: 'tableOpenOprate',
  ppcOpenOprate: 'ppcOpenOprate',
  exchangTable: 'exchangTable',
  advertImage: 'advertImage',
  setLinkedId: 'setLinkedId',
  pushmsgPop: 'pushmsgPop',
  timing: 'Timing',
  GetBroadcast: 'GetBroadcast',
  sendBroadcast: 'sendBroadcast',
  requestpushmsgPop: 'requestpushmsgPop',
  Message: 'Message',
  Logout: 'Logout',
  imageUpload: 'img_upload',
} as const

type TopicKey = {
  topic: string
}

class MqttClient {
  client: mqtt.MqttClient | null
  isConnected: boolean
  limitConnect: number
  timeConnect: number
  sendQueue = new Map<TopicKey, string>()
  subscribedTopics = new Set<string>()

  constructor() {
    this.client = null
    this.isConnected = false
    this.limitConnect = 3
    this.timeConnect = 0
  }

  async connect(callback?: (data: any) => void) {
    try {
      // 添加MQTT连接选项，包括用户名和密码
      const options = {
        username: appConfig.REACT_APP_mqtt_username,
        password: appConfig.REACT_APP_mqtt_password,
        // RabbitMQ 使用一个内部参数：mqtt.client_idle_timeout 作为超时判断，其默认值为 60s
        // 这里设置需要小于 60s 才能在 RabbitMQ 没有判定超时前，发送出心跳包以维持连接
        keepalive: 30,
        reconnectPeriod: 2000,
        clientId:
          'mqttjs_' +
          Math.random()
            .toString(16)
            .substr(2, 8),
        connectTimeout: 30000, // 增加连接超时时间
      }

      this.client = mqtt.connect(appConfig.REACT_APP_mqtt_baseUrl, options)

      this.client.on('connect', async () => {
        console.log(
          '%cMQTT连接成功!',
          'color: green; font-weight: bold; font-size: 16px',
        )
        this.isConnected = true

        // 打印连接详情
        console.log('MQTT客户端详情:', {
          client: this.client ? '已初始化' : '未初始化',
          clientId: options.clientId,
        })

        // 获取linked并订阅相关主题
        // const linked = await getLinked()
        // console.log('当前linked ID:', linked)
        // if (linked) {
        //   this.subscribeToLinkedTopics(linked)
        // } else {
        //   console.warn('未获取到linked ID，无法订阅主题')
        // }

        typeof callback === 'function' &&
          callback({ type: 'connect', success: true })

        if (this.sendQueue.size > 0) {
          this.sendQueue.forEach((value, key) => {
            this.client && this.publish(key.topic, value)
          })
          this.sendQueue.clear()
        }
      })

      this.client.on('message', (topic: string, message: Buffer) => {
        console.log(`收到MQTT消息，主题: ${topic}`)
        console.log(`消息内容: ${message.toString()}`)

        if (isNil(message)) {
          console.warn('MQTT消息为空')
          return
        }

        try {
          let data
          try {
            data = JSON.parse(message.toString())
            console.log('解析后的数据:', data)
          } catch (parseError) {
            console.warn(
              '消息不是有效的JSON格式，尝试使用消息内容作为非JSON数据',
            )
            console.log('原始消息:', message.toString())
            // 尝试使用消息内容作为非JSON数据
            data = { content: message.toString() }
          }

          if (data.error_msg) {
            console.error('MQTT错误消息:', data.error_msg)
            ToastFail(data.error_msg)
            return
          }

          // 从主题中提取事件类型
          const topicParts = topic.split('/')
          const msgType = topicParts[topicParts.length - 1]
          console.log(`触发事件: ${msgType}`)

          eventEmitter.emit(msgType, data)
        } catch (error) {
          console.error('处理MQTT消息时出错:', error)
        }
      })

      this.client.on('error', (err: Error) => {
        console.error('MQTT连接错误:', err.message)
        console.error('错误详情:', err)
        this.isConnected = false
      })

      this.client.on('close', () => {
        this.isConnected = false
        console.log('MQTT connection closed')

        if (this.limitConnect > 0) {
          this.limitConnect--
          this.timeConnect++
          console.log('第' + this.timeConnect + '次重连')

          setTimeout(() => {
            this.connect(async () => {
              this.limitConnect = 3
              this.timeConnect = 0

              // const linked = await getLinked()
              // if (linked) {
              //   this.subscribeToLinkedTopics(linked)
              // }
            })
          }, 2000)
        } else {
          ToastFail('系统连接超时，请刷新页面重试')
        }
      })
    } catch (error) {
      console.error('MQTT连接失败:', error)
      this.isConnected = false
    }
  }

  // async subscribeToLinkedTopics(linked: string) {
  //   if (!this.client || !linked) return

  //   console.log('开始订阅主题，linked ID:', linked)

  //   // 取消之前的所有订阅
  //   if (this.subscribedTopics.size > 0) {
  //     console.log('清除之前的主题订阅...')
  //     const topics = Array.from(this.subscribedTopics)
  //     for (const topic of topics) {
  //       this.client.unsubscribe(topic)
  //       console.log(`已取消订阅: ${topic}`)
  //     }
  //     this.subscribedTopics.clear()
  //   }

  //   // 根据Notepad中的MQTT协议定义订阅主题
  //   const topics = [
  //     // `${linked}/frontInfo`, // 后台管理中的Front使用
  //     // `${linked}/hallInfo`, // 后台管理中的Hall使用
  //     // `${linked}/ppc`, // 后台管理中的PPC使用
  //     // `${linked}/playModeStatus`, // 当前播放状态 (包房模式和大厅模式下使用)
  //     // `${linked}/orderList`, // 已点列表    (包房模式下使用)
  //     // `${linked}/tablePlayList`, // 播放序列    (大厅模式下使用)
  //     // `${linked}/tableAllList`, // 全部列表    (大厅模式下使用)
  //   ]

  //   // TODO:也可以添加特定桌台的订阅，如果有需要
  //   // const selectedTable = "T5" // 这可能需要从某处获取
  //   // topics.push(`${linked}/orderList/${selectedTable}`)

  //   topics.forEach(topic => {
  //     if (this.client) {
  //       this.client.subscribe(topic, (err: Error | null) => {
  //         if (!err) {
  //           console.log(`成功订阅主题: ${topic}`)
  //           this.subscribedTopics.add(topic)
  //         } else {
  //           console.error(`订阅主题失败: ${topic}`, err)
  //         }
  //       })
  //     }
  //   })
  // }

  async uploadImage(imageData: ArrayBuffer) {
    const token = await getToken()
    if (!this.client) {
      console.error('MQTT客户端未初始化，无法上传图片')
      return
    }

    if (!this.isConnected) {
      console.error('MQTT未连接，无法上传图片')
      return
    }

    try {
      const linked = await getLinked()
      if (!linked) {
        console.error('未获取到linked，无法上传图片')
        return
      }

      // 构建消息前缀：img_upload + 32位token
      const prefix = MQTT_MSG_KEYS.imageUpload + token
      const prefixBytes = new TextEncoder().encode(prefix)

      // 创建一个新的ArrayBuffer，大小为前缀字节长度加上图片数据长度
      const combinedBuffer = new Uint8Array(
        prefixBytes.length + imageData.byteLength,
      )

      // 复制前缀数据到新的ArrayBuffer
      combinedBuffer.set(prefixBytes, 0)
      // 复制图片数据到新的ArrayBuffer
      combinedBuffer.set(new Uint8Array(imageData), prefixBytes.length)

      // 转换为Buffer并发送二进制数据到MQTT服务器，不经过JSON处理
      const buffer = Buffer.from(combinedBuffer)
      this.client.publish(linked, buffer)
      console.log('已发送图片上传请求:', {
        linked,
        prefixLength: prefixBytes.length,
        totalLength: buffer.length,
      })

      return true
    } catch (error) {
      console.error('上传图片时发生错误:', error)
      return false
    }
  }

  async publish(topic: string, data = {}) {
    if (!this.client) return

    const params = await handleRequestData({
      url: topic,
      data,
    })

    if (this.isConnected && this.client) {
      this.client.publish(topic, JSON.stringify(params))
      console.log('发送MQTT消息:', { topic, params })
    } else {
      console.log('MQTT is not connected')
      this.sendQueue.set({ topic }, JSON.stringify(params))
    }
  }

  async send(topicSuffix: string, data = {}) {
    const linked = await getLinked()
    if (!linked) {
      console.error('未获取到linked，无法发送消息')
      return
    }

    const topic = `${linked}/${topicSuffix}`

    this.publish(topic, data)
  }

  async subscribe(topic: string, callback: (data: any) => void) {
    if (this.client) {
      if (!topic.includes('/')) {
        const linked = await getLinked()
        const userInfo = await getUserInfo()
        if (!linked) {
          console.error('未获取到linked，无法订阅主题')
          return
        }
        // 如果连接的是桌台，则订阅 orderList 主题时需要带上桌台名称
        topic =
          `${linked}/${topic}` +
          (topic === MQTT_MSG_KEYS.orderList && userInfo.type === 'table'
            ? `/${userInfo.name}`
            : '')
      }
      this.client.subscribe(topic, (err: Error | null) => {
        if (!err) {
          console.log(`已订阅主题: ${topic}`)
          this.subscribedTopics.add(topic)
        } else {
          console.error(`订阅主题失败: ${topic}`, err)
        }
      })
    }

    let handler = (data: any) => {
      typeof callback === 'function' && callback(data)
    }

    // 从主题中提取事件类型
    const topicParts = topic.split('/')
    const eventName = topicParts[topicParts.length - 1]

    eventEmitter.addListener(eventName, handler)
  }

  async unsubscribe(topic: string) {
    if (this.client) {
      if (!topic.includes('/')) {
        const linked = await getLinked()
        const userInfo = await getUserInfo()
        if (!linked) {
          console.error('未获取到linked，无法订阅主题')
          return
        }
        // 如果连接的是桌台，则取消订阅 orderList 主题时需要带上桌台名称
        topic =
          `${linked}/${topic}` +
          (topic === MQTT_MSG_KEYS.orderList && userInfo.type === 'table'
            ? `/${userInfo.name}`
            : '')
      }
      this.client.unsubscribe(topic)
      console.log(`已取消MQTT客户端订阅: ${topic}`)
    }

    this.subscribedTopics.delete(topic)
    console.log(`已从订阅主题集合中移除: ${topic}`)

    // 从主题中提取事件类型
    const topicParts = topic.split('/')
    const eventName = topicParts[topicParts.length - 1]

    eventEmitter.removeAllListeners(eventName)
    console.log(`已移除事件监听器: ${eventName}`)
  }

  async unsubscribeAll() {
    if (!this.client) return

    // 取消之前的所有订阅
    if (this.subscribedTopics.size > 0) {
      console.log('清除之前的主题订阅...')
      const topics = Array.from(this.subscribedTopics)
      for (const topic of topics) {
        this.client.unsubscribe(topic)
        console.log(`已取消订阅: ${topic}`)
      }
      this.subscribedTopics.clear()
    }
  }

  on(eventName: string, callback: (data: any) => void) {
    let handler = (data: any) => {
      typeof callback === 'function' && callback(data)
    }

    eventEmitter.addListener(eventName, handler)
  }

  off(eventName: string) {
    eventEmitter.removeAllListeners(eventName)
  }
}

const $mqtt = new MqttClient()

export default $mqtt

export function checkMqttConnection() {
  if ($mqtt && $mqtt.isConnected) {
    console.log('MQTT连接状态: 已连接')
    return true
  } else {
    console.warn('MQTT连接状态: 未连接')
    return false
  }
}
